/**
 * Is this run happening *right now*, and in what phase?
 *
 * The answer is not simply `status === 'RUNNING'`, because the executor and
 * the webhook receiver both record progress ahead of the water:
 *
 * - A single-cycle run is marked COMPLETED the moment the controller accepts
 *   the command, with `actualEnd` set to when the valve will close. COMPLETED
 *   with `actualEnd` in the future therefore means water is flowing now.
 * - A multi-cycle (cycle-and-soak) run stays RUNNING across executor ticks,
 *   with `nextPulseAt` = end of the current pulse + that cycle's soak. When the
 *   record carries `pulses` those windows say outright when the valve closed;
 *   otherwise subtracting the soak recovers it. Either way RUNNING splits into
 *   a watering phase and a soaking phase.
 * - A run started outside this app is opened RUNNING by the webhook with no
 *   pulse clock at all; its `actualEnd` is where the run is headed, when
 *   Rachio reported a duration.
 * - A new-seed misting record stays RUNNING for the WHOLE local day, from its
 *   first mist until the last cycle is dealt with, with `nextPulseAt` at the
 *   next cycle's scheduled time. RUNNING there says almost nothing about now:
 *   the valve is open for a couple of minutes per cycle and shut for the hours
 *   in between, and a cycle that missed its slot never opened it at all.
 *
 * These are facts about how runs are written, so they live here with the run
 * types rather than in whichever client wants to display them. Anything that
 * asks "is water moving?" — the dashboard today, notifications or the executor
 * tomorrow — must agree, and agreeing means sharing this function.
 */
/**
 * How long past its expected end a run is still believed live. The executor
 * ticks every 5 minutes and closes runs out as it goes; beyond this a record
 * is likely stale (a crashed tick, or a completion webhook that never
 * arrived), and claiming "still watering" forever would be a lie.
 */
export const STALE_RUN_GRACE_MS = 30 * 60 * 1000;
/**
 * Where a single run stands at `now` (epoch ms), or undefined when it is not
 * in flight. Dry runs never report activity: no valve ever opened. An
 * `unmanaged` run — the controller watering a zone we never imported — DOES
 * report activity: its valve really is open, and saying so is the whole reason
 * the record exists (we just can't measure its water).
 */
export function runActivity(run, now) {
    if (run.dryRun)
        return undefined;
    const end = run.actualEnd ? Date.parse(run.actualEnd) : NaN;
    if (run.status === 'RUNNING') {
        const next = run.nextPulseAt ? Date.parse(run.nextPulseAt) : NaN;
        // No pulse clock: a run started outside this app, opened by the webhook.
        if (Number.isNaN(next)) {
            if (!Number.isNaN(end))
                return now < end ? { phase: 'WATERING', until: end, run } : undefined;
            // Duration unknown — believed live only for as long as a lost close event
            // stays plausible.
            const start = run.actualStart ? Date.parse(run.actualStart) : NaN;
            if (Number.isNaN(start) || now > start + STALE_RUN_GRACE_MS)
                return undefined;
            return { phase: 'WATERING', run };
        }
        const started = run.cyclesStarted ?? 0;
        const pulses = run.pulses ?? [];
        // When the record has real valve windows, the last one IS the current
        // pulse, no recovery needed. For a session run that window is the same
        // estimate the fallback computes; for a misting record fired late inside
        // its grace period the two are up to 90 minutes apart, and only the pulse
        // says where the water actually went.
        const lastPulseEnd = pulses.length > 0 ? Date.parse(pulses[pulses.length - 1].end) : NaN;
        // Fallback for records written before pulse windows existed: the soak
        // belongs to the cycle that just fired, so subtracting it recovers the
        // valve-close time.
        const soakMs = (run.plan.cycles[started - 1]?.soakMinutes ?? 0) * 60_000;
        const pulseEnd = Number.isNaN(lastPulseEnd) ? next - soakMs : lastPulseEnd;
        // An open valve window is positive evidence and outranks everything below.
        if (now < pulseEnd)
            return { phase: 'WATERING', until: pulseEnd, run };
        // A misting record (the only kind carrying `seedTimes`) appends a pulse
        // only for a cycle that really FIRED; one that missed its slot is counted
        // as handled with no pulse at all. Fewer pulses than handled cycles
        // therefore means the current cycle expired unwatered: no valve opened, so
        // there is nothing to soak from, and the hours until the next mist are not
        // a soak by any reading.
        if (run.seedTimes !== undefined && pulses.length < started)
            return undefined;
        if (now < next + STALE_RUN_GRACE_MS)
            return { phase: 'SOAKING', until: next, run };
        return undefined;
    }
    if (run.status === 'COMPLETED' && !Number.isNaN(end) && end > now) {
        return { phase: 'WATERING', until: end, run };
    }
    return undefined;
}
/**
 * Every run in flight at `now`, soonest to finish first. Runs whose end is
 * unknown sort last — there is nothing to order them by.
 */
export function activeRuns(runs, now) {
    return runs
        .map((run) => runActivity(run, now))
        .filter((a) => a !== undefined)
        .sort((a, b) => (a.until ?? Infinity) - (b.until ?? Infinity));
}
//# sourceMappingURL=run-status.js.map